import loadable from '@loadable/component';
import { useEffect, useState, useCallback } from 'react';
import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import useUser from '@/hooks/useUser';
import { getInfo } from './utils';
import type { LimitedType } from './utils';
import { useUserAccess } from '@/hooks/useUserAccess';

const PlansModal = loadable(
  () => import('@/components/OurPlans/components/PlansModal/PlansModal'),
  { ssr: false }
);

interface Props {
  portfolio: Portfolio;
  limitedType: LimitedType;
}
export const RestrictionPlanModal = ({ portfolio, limitedType }: Props) => {
  const [showPlanModal, setShowPlanModal] = useState(false);
  const { data: user, mutate: mutateUser, isFetched } = useUser();
  const { data: access } = useUserAccess();

  const {
    dismissedKey,
    dissmissedKeyForUpdate,
    limitHitterEventProperties,
    delayTime,
    headerText,
  } = getInfo(limitedType);

  const handlePlanModalAction = useCallback(() => {
    mutateUser({
      settings: {
        portfolio: {
          [dissmissedKeyForUpdate]: true,
        },
      },
    });
  }, [dissmissedKeyForUpdate, mutateUser]);

  useEffect(() => {
    const hasDismissed = user.portfolio?.[dismissedKey];
    let timeoutId;
    if (
      isFetched &&
      access.planType !== 'unlimited' &&
      RUNTIME_ENV !== 'server' &&
      hasDismissed !== true
    ) {
      timeoutId = setTimeout(() => {
        setShowPlanModal(true);
      }, delayTime);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    access.planType,
    delayTime,
    dismissedKey,
    isFetched,
    limitedType,
    portfolio.isAnalysisRestricted,
    user.hasSeenTrialEndModal,
    user.portfolio,
  ]);

  return (
    <>
      {showPlanModal && (
        <PlansModal
          onClose={() => setShowPlanModal(false)}
          onAction={handlePlanModalAction}
          limitHitterEventProperties={limitHitterEventProperties}
        >
          <p data-cy-id="plan-restriction">
            You&apos;ve reached your portfolio limits on your {access.planType}{' '}
            plan
          </p>

          <h2>{headerText}</h2>
        </PlansModal>
      )}
    </>
  );
};
